import {
  Alert,
  Box,
  Button,
  Card,
  Chip,
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  IconButton,
  Stack,
  Tab,
  Tabs,
  TextField,
  Tooltip,
  Typography,
} from '@mui/material';
import {
  CalendarMonthRounded,
  ChevronLeftRounded,
  ChevronRightRounded,
  ContentCopyRounded,
  RestartAltRounded,
  RssFeedRounded,
} from '@mui/icons-material';
import { useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useConfirm } from '../../../components/ConfirmProvider';
import { usePortalCalendar, type FlatCalendarEvent } from '../hooks/use-portal-data';
import { useClassroomOn } from '../hooks/use-classroom-on';
import { useTimeRange } from '../components/ScheduleText';
import { calendarDay, localYmd } from '../util/calendar-dates';
import { PortalEmpty, PortalLoading } from '../PortalShell';
import { courseColor } from './CourseShell';
import { useNavigate } from 'react-router-dom';
import { useTenantStore } from '../../../stores/tenant.store';
import { errorText } from '../../../lib/error-text';
import {
  buildIcalFeedUrl,
  buildWebcalUrl,
  calendarSubscriptionApi,
} from '../../../lib/api/calendar-subscription.api';
import { formatDate } from '../../../lib/i18n-format';
import { usePortalViewing } from '../../../stores/portal-view.store';
import { useTenantTimeZone } from '../../../stores/tenant.store';

type ViewMode = 'agenda' | 'week';

function startOfWeek(d: Date): Date {
  const day = d.getDay() || 7;
  const monday = new Date(d);
  monday.setDate(d.getDate() - day + 1);
  monday.setHours(0, 0, 0, 0);
  return monday;
}

function addDays(d: Date, n: number): Date {
  const x = new Date(d);
  x.setDate(x.getDate() + n);
  return x;
}

/** Cómo se leen las fechas: días de CALENDARIO, con aula y sin ella (M4·B1;
 *  ver util/calendar-dates). */
interface DayCodec {
  ymd: (d: Date) => string;
  day: (iso: string) => Date;
}
const CALENDAR_DAYS: DayCodec = { ymd: localYmd, day: calendarDay };

const DAY_LABEL_KEYS = [
  'dayMon',
  'dayTue',
  'dayWed',
  'dayThu',
  'dayFri',
  'daySat',
  'daySun',
] as const;

export function CalendarPage(): JSX.Element {
  const { t } = useTranslation('sis');
  const [mode, setMode] = useState<ViewMode>('agenda');
  const [anchor, setAnchor] = useState(() => new Date());
  const [subscribeOpen, setSubscribeOpen] = useState(false);
  // «Ver portal»: la suscripción crea o rota un token del alumno; no va.
  const viewingPortal = usePortalViewing();

  // Sin aula (g4), las clases salen del horario de cada sección: fechas de
  // calendario del seminario. Con aula, todo como siempre.
  const classroom = useClassroomOn();
  const days = CALENDAR_DAYS;
  const timeZone = useTenantTimeZone();

  // Date window: agenda = 30 days fwd, week = 7 days centered
  const { fromDate, toDate } = useMemo(() => {
    if (mode === 'week') {
      const start = startOfWeek(anchor);
      return { fromDate: days.ymd(start), toDate: days.ymd(addDays(start, 6)) };
    }
    return { fromDate: days.ymd(anchor), toDate: days.ymd(addDays(anchor, 30)) };
  }, [mode, anchor, days]);

  const { data, isLoading, source } = usePortalCalendar(fromDate, toDate);
  const fromSchedule = source === 'schedule';

  if (isLoading || !classroom.settled) return <PortalLoading />;

  return (
    <Stack spacing={3}>
      <Stack
        direction={{ xs: 'column', sm: 'row' }}
        alignItems={{ xs: 'flex-start', sm: 'center' }}
        justifyContent="space-between"
        spacing={1.5}
      >
        <Stack direction="row" alignItems="center" spacing={1.5}>
          <Typography variant="h4" fontWeight={700} color="#e6edf3">
            {t('portal.calendar.title')}
          </Typography>
          {!viewingPortal && (
            <Tooltip title={t('portal.calendar.subscribeTooltip')}>
              <Button
                size="small"
                variant="outlined"
                startIcon={<RssFeedRounded />}
                onClick={() => setSubscribeOpen(true)}
                sx={{
                  color: '#a5b4fc',
                  borderColor: '#4338ca',
                  textTransform: 'none',
                  '&:hover': { borderColor: '#6366f1', bgcolor: 'rgba(99,102,241,0.08)' },
                }}
              >
                {t('portal.calendar.subscribe')}
              </Button>
            </Tooltip>
          )}
        </Stack>
        <Tabs
          value={mode}
          onChange={(_, v: ViewMode) => setMode(v)}
          textColor="inherit"
          variant="scrollable"
          allowScrollButtonsMobile
          TabIndicatorProps={{ style: { backgroundColor: '#6366f1' } }}
        >
          <Tab
            value="agenda"
            label={t('portal.calendar.viewAgenda')}
            sx={{ color: '#8b949e', textTransform: 'none' }}
          />
          <Tab
            value="week"
            label={t('portal.calendar.viewWeek')}
            sx={{ color: '#8b949e', textTransform: 'none' }}
          />
        </Tabs>
      </Stack>

      <Stack direction="row" alignItems="center" spacing={1} flexWrap="wrap" useFlexGap>
        <IconButton
          onClick={() => setAnchor(addDays(anchor, mode === 'week' ? -7 : -30))}
          sx={{ color: '#8b949e' }}
        >
          <ChevronLeftRounded />
        </IconButton>
        <Typography
          variant="body1"
          color="#c9d1d9"
          sx={{ flex: 1, minWidth: { xs: 0, sm: 220 }, textAlign: { xs: 'center', sm: 'left' } }}
        >
          {mode === 'week'
            ? t('portal.calendar.weekOf', {
                date: formatDate(startOfWeek(anchor), {
                  day: 'numeric',
                  month: 'long',
                }),
              })
            : t('portal.calendar.fromDate', {
                date: formatDate(anchor, { day: 'numeric', month: 'long' }),
              })}
        </Typography>
        <IconButton
          onClick={() => setAnchor(addDays(anchor, mode === 'week' ? 7 : 30))}
          sx={{ color: '#8b949e' }}
        >
          <ChevronRightRounded />
        </IconButton>
        <Chip
          size="small"
          label={t('portal.calendar.today')}
          onClick={() => setAnchor(new Date())}
          variant="outlined"
          sx={{ borderColor: '#30363d', color: '#c9d1d9', cursor: 'pointer' }}
        />
      </Stack>

      {fromSchedule && (
        <Typography variant="caption" color="#8b949e" sx={{ mt: '-12px !important' }}>
          {t('portal.schedule.zoneNote', { zone: timeZone })}
        </Typography>
      )}

      {mode === 'agenda' ? (
        <AgendaView events={data} days={days} fromSchedule={fromSchedule} />
      ) : (
        <WeekView
          events={data}
          weekStart={startOfWeek(anchor)}
          days={days}
          fromSchedule={fromSchedule}
        />
      )}

      {subscribeOpen && !viewingPortal && (
        <SubscribeDialog onClose={() => setSubscribeOpen(false)} />
      )}
    </Stack>
  );
}

function SubscribeDialog({ onClose }: { onClose: () => void }): JSX.Element {
  const { t } = useTranslation('sis');
  const confirm = useConfirm();
  const { currentTenantId } = useTenantStore();
  const [token, setToken] = useState<string | null>(null);
  const [loading, setLoading] = useState(true);
  const [regenerating, setRegenerating] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [copied, setCopied] = useState<'webcal' | 'https' | null>(null);

  // Lazy load token on dialog open
  useMemo(() => {
    if (!currentTenantId) return;
    setLoading(true);
    calendarSubscriptionApi
      .getMyToken({
        tenantId: currentTenantId,
      })
      .then(({ token: tok }) => setToken(tok))
      .catch((e) => setError(errorText(e, t('portal.calendar.errorFetchToken'))))
      .finally(() => setLoading(false));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const regenerate = async () => {
    if (!currentTenantId) return;
    if (!(await confirm({ message: t('portal.calendar.regenerateConfirm') }))) return;
    setRegenerating(true);
    setError(null);
    try {
      const r = await calendarSubscriptionApi.regenerateMyToken({
        tenantId: currentTenantId,
      });
      setToken(r.token);
    } catch (e) {
      setError(errorText(e, t('portal.calendar.errorRegenerate')));
    } finally {
      setRegenerating(false);
    }
  };

  const copy = async (text: string, which: 'webcal' | 'https') => {
    try {
      await navigator.clipboard.writeText(text);
      setCopied(which);
      setTimeout(() => setCopied(null), 1800);
    } catch {
      // ignore — user can still select manually
    }
  };

  const webcalUrl = token ? buildWebcalUrl(token) : '';
  const httpsUrl = token ? buildIcalFeedUrl(token) : '';

  return (
    <Dialog
      open
      onClose={onClose}
      maxWidth="sm"
      fullWidth
      PaperProps={{
        sx: { bgcolor: '#0d1117', border: '1px solid #21262d', borderRadius: 2 },
      }}
    >
      <DialogTitle sx={{ color: '#e6edf3' }}>{t('portal.calendar.subscribeTitle')}</DialogTitle>
      <DialogContent>
        <Typography variant="body2" color="#8b949e" sx={{ mb: 2 }}>
          {t('portal.calendar.subscribeIntro')}
        </Typography>

        {loading ? (
          <Box textAlign="center" py={4}>
            <CircularProgress size={24} />
          </Box>
        ) : error ? (
          <Alert severity="error">{error}</Alert>
        ) : token ? (
          <Stack spacing={2}>
            <Box>
              <Typography
                variant="caption"
                color="#8b949e"
                fontWeight={700}
                sx={{ mb: 0.5, display: 'block' }}
              >
                {t('portal.calendar.webcalLabel')}
              </Typography>
              <Stack direction="row" spacing={1} alignItems="center">
                <TextField
                  value={webcalUrl}
                  size="small"
                  fullWidth
                  InputProps={{ readOnly: true, sx: { fontFamily: 'monospace', fontSize: 12 } }}
                />
                <Tooltip
                  title={
                    copied === 'webcal'
                      ? t('portal.calendar.copied')
                      : t('portal.calendar.copyLink')
                  }
                >
                  <IconButton
                    onClick={() => copy(webcalUrl, 'webcal')}
                    sx={{ color: copied === 'webcal' ? '#10b981' : '#8b949e' }}
                  >
                    <ContentCopyRounded fontSize="small" />
                  </IconButton>
                </Tooltip>
              </Stack>
              <Button
                size="small"
                href={webcalUrl}
                sx={{ mt: 1, textTransform: 'none', color: '#a5b4fc' }}
              >
                {t('portal.calendar.openInApp')}
              </Button>
            </Box>

            <Box>
              <Typography
                variant="caption"
                color="#8b949e"
                fontWeight={700}
                sx={{ mb: 0.5, display: 'block' }}
              >
                {t('portal.calendar.httpsLabel')}
              </Typography>
              <Stack direction="row" spacing={1} alignItems="center">
                <TextField
                  value={httpsUrl}
                  size="small"
                  fullWidth
                  InputProps={{ readOnly: true, sx: { fontFamily: 'monospace', fontSize: 12 } }}
                />
                <Tooltip
                  title={
                    copied === 'https' ? t('portal.calendar.copied') : t('portal.calendar.copyLink')
                  }
                >
                  <IconButton
                    onClick={() => copy(httpsUrl, 'https')}
                    sx={{ color: copied === 'https' ? '#10b981' : '#8b949e' }}
                  >
                    <ContentCopyRounded fontSize="small" />
                  </IconButton>
                </Tooltip>
              </Stack>
            </Box>

            <Alert severity="info" sx={{ bgcolor: 'rgba(99,102,241,0.08)' }}>
              {t('portal.calendar.privacyWarning')}
            </Alert>
          </Stack>
        ) : null}
      </DialogContent>
      <DialogActions sx={{ px: 3, pb: 2, justifyContent: 'space-between' }}>
        <Button
          size="small"
          startIcon={
            regenerating ? <CircularProgress size={14} color="inherit" /> : <RestartAltRounded />
          }
          onClick={regenerate}
          disabled={regenerating || !token}
          sx={{ color: '#f59e0b', textTransform: 'none' }}
        >
          {t('portal.calendar.regenerateLink')}
        </Button>
        <Button onClick={onClose}>{t('portal.calendar.close')}</Button>
      </DialogActions>
    </Dialog>
  );
}

function AgendaView({
  events,
  days,
  fromSchedule,
}: {
  events: FlatCalendarEvent[];
  days: DayCodec;
  fromSchedule: boolean;
}): JSX.Element {
  const { t } = useTranslation('sis');
  const navigate = useNavigate();
  const range = useTimeRange();
  // «Ver portal»: sin suscripción al calendario (crea un token) ni detalle de tarea.
  const viewing = usePortalViewing();
  if (events.length === 0) {
    // Sin aula no hay syllabus ni entregas: el vacío no habla de ellos.
    return fromSchedule ? (
      <PortalEmpty
        icon={<CalendarMonthRounded fontSize="inherit" />}
        title={t('portal.calendar.emptyNoClassroom')}
      />
    ) : (
      <PortalEmpty
        icon={<CalendarMonthRounded fontSize="inherit" />}
        title={t('portal.calendar.emptyTitle')}
        body={t('portal.calendar.emptyBody')}
      />
    );
  }
  // Group by date
  const byDate = new Map<string, FlatCalendarEvent[]>();
  for (const e of events) {
    const arr = byDate.get(e.date) ?? [];
    arr.push(e);
    byDate.set(e.date, arr);
  }
  const sortedDates = [...byDate.keys()].sort();

  const todayIso = days.ymd(new Date());

  return (
    <Stack spacing={2}>
      {sortedDates.map((iso) => {
        const day = days.day(iso);
        const isToday = iso === todayIso;
        return (
          <Box key={iso}>
            <Stack direction="row" spacing={1.5} alignItems="center" mb={1}>
              <Box
                sx={{
                  width: 56,
                  textAlign: 'center',
                  py: 0.5,
                  bgcolor: isToday ? '#6366f1' : 'transparent',
                  border: isToday ? 'none' : '1px solid #21262d',
                  borderRadius: 1,
                }}
              >
                <Typography
                  variant="caption"
                  color={isToday ? '#fff' : '#8b949e'}
                  sx={{ textTransform: 'uppercase', fontSize: 10 }}
                >
                  {formatDate(day, { month: 'short' })}
                </Typography>
                <Typography variant="h6" fontWeight={700} color={isToday ? '#fff' : '#c9d1d9'}>
                  {day.getDate()}
                </Typography>
              </Box>
              <Typography variant="caption" color="#8b949e" sx={{ textTransform: 'capitalize' }}>
                {formatDate(day, { weekday: 'long' })}
              </Typography>
            </Stack>
            <Card sx={{ bgcolor: '#161b22', border: '1px solid #21262d', borderRadius: 2 }}>
              {(byDate.get(iso) ?? []).map((evt, i, arr) => {
                const color = courseColor(evt.sectionId);
                const clickable = evt.type === 'assignment';
                return (
                  <Box
                    key={`${evt.type}-${evt.id}`}
                    onClick={() => {
                      if (clickable)
                        if (!viewing)
                          navigate(`/portal-v2/courses/${evt.sectionId}/assignments/${evt.id}`);
                    }}
                    sx={{
                      px: 2.5,
                      py: 1.25,
                      borderBottom: i === arr.length - 1 ? 'none' : '1px solid #21262d',
                      display: 'flex',
                      alignItems: 'center',
                      gap: 1.5,
                      cursor: clickable ? 'pointer' : 'default',
                      '&:hover': clickable ? { bgcolor: 'rgba(255,255,255,0.03)' } : {},
                    }}
                  >
                    <Box sx={{ width: 3, height: 24, bgcolor: color, borderRadius: 2 }} />
                    {evt.startTime && (
                      <Typography
                        variant="caption"
                        color="#8b949e"
                        sx={{ fontFamily: 'monospace', minWidth: 80 }}
                      >
                        {range(evt.startTime, evt.endTime)}
                      </Typography>
                    )}
                    <Box sx={{ flex: 1 }}>
                      <Typography variant="body2" fontWeight={600} color="#e6edf3" noWrap>
                        {evt.title}
                      </Typography>
                      <Typography variant="caption" color="#8b949e">
                        {evt.courseCode}
                        {evt.location && ` · ${evt.location}`}
                        {evt.scriptureRefs &&
                          ` · ${evt.scriptureRefs
                            .map((r) => `${r.book} ${r.chapter}${r.verses ? `:${r.verses}` : ''}`)
                            .join(', ')}`}
                      </Typography>
                    </Box>
                    <Chip
                      size="small"
                      label={
                        evt.startOnly
                          ? t('portal.schedule.startOnly')
                          : evt.type === 'session'
                            ? t('portal.calendar.eventSession')
                            : t('portal.calendar.eventAssignment')
                      }
                      variant="outlined"
                      sx={{ borderColor: '#30363d', height: 20, fontSize: 11 }}
                    />
                  </Box>
                );
              })}
            </Card>
          </Box>
        );
      })}
    </Stack>
  );
}

function WeekView({
  events,
  weekStart,
  days: codec,
  fromSchedule,
}: {
  events: FlatCalendarEvent[];
  weekStart: Date;
  days: DayCodec;
  fromSchedule: boolean;
}): JSX.Element {
  const { t } = useTranslation('sis');
  const range = useTimeRange();
  const days = Array.from({ length: 7 }, (_, i) => addDays(weekStart, i));
  const todayIso = codec.ymd(new Date());

  return (
    <Stack spacing={1}>
      <Box
        sx={{
          display: 'grid',
          gridTemplateColumns: { xs: '1fr', sm: 'repeat(7, 1fr)' },
          gap: 1,
          bgcolor: '#161b22',
          border: '1px solid #21262d',
          borderRadius: 2,
          p: 1.5,
          overflow: 'auto',
        }}
      >
        {days.map((day, idx) => {
          const iso = codec.ymd(day);
          const dayEvents = events.filter((e) => e.date === iso);
          const isToday = iso === todayIso;
          return (
            <Box key={iso} sx={{ minWidth: { xs: 0, sm: 130 } }}>
              <Box
                sx={{
                  textAlign: 'center',
                  py: 0.5,
                  mb: 1,
                  bgcolor: isToday ? '#6366f1' : 'transparent',
                  borderRadius: 1,
                }}
              >
                <Typography variant="caption" color={isToday ? '#fff' : '#8b949e'}>
                  {t(`portal.calendar.${DAY_LABEL_KEYS[idx]}`)}
                </Typography>
                <Typography variant="body1" fontWeight={700} color={isToday ? '#fff' : '#c9d1d9'}>
                  {day.getDate()}
                </Typography>
              </Box>
              <Stack spacing={0.5} sx={{ minHeight: { xs: 0, sm: 200 } }}>
                {dayEvents.map((evt) => {
                  const color = courseColor(evt.sectionId);
                  return (
                    <Box
                      key={`${evt.type}-${evt.id}`}
                      sx={{
                        bgcolor: 'rgba(99,102,241,0.08)',
                        borderLeft: `3px solid ${color}`,
                        borderRadius: 1,
                        p: 0.75,
                      }}
                    >
                      {evt.startTime && (
                        <Typography
                          variant="caption"
                          color="#8b949e"
                          sx={{ fontFamily: 'monospace', fontSize: 10 }}
                        >
                          {range(evt.startTime, evt.endTime)}
                        </Typography>
                      )}
                      <Typography
                        variant="caption"
                        color="#e6edf3"
                        display="block"
                        sx={{ fontSize: 11, fontWeight: 600 }}
                      >
                        {evt.title}
                      </Typography>
                      <Typography variant="caption" color="#8b949e" sx={{ fontSize: 10 }}>
                        {evt.courseCode}
                        {evt.location && ` · ${evt.location}`}
                      </Typography>
                      {/* Horario importado sin término: un bloque corto, sin
                        duración inventada, que lo dice. */}
                      {evt.startOnly && (
                        <Typography
                          variant="caption"
                          display="block"
                          sx={{ fontSize: 10, color: '#fbbf24' }}
                        >
                          {t('portal.schedule.startOnly')}
                        </Typography>
                      )}
                    </Box>
                  );
                })}
              </Stack>
            </Box>
          );
        })}
      </Box>
      {fromSchedule && events.length === 0 && (
        <Typography variant="body2" color="#8b949e" textAlign="center">
          {t('portal.calendar.emptyNoClassroom')}
        </Typography>
      )}
    </Stack>
  );
}
